import { useState } from 'react'
import { CalendarDays, FileText, FlaskConical, Pill, Stethoscope, Activity } from 'lucide-react'
import type { EvenementTimeline } from '../../lib/types'
import { Carte, EtatVide, Pastille } from '../base'
import VisionneuseDocument from '../VisionneuseDocument'
import * as f from '../../lib/format'

const NATURES = {
  venue: { libelle: 'Venue', icone: CalendarDays },
  document: { libelle: 'Document', icone: FileText },
  histoire: { libelle: 'Histoire de la maladie', icone: Activity },
  bilan: { libelle: 'Biologie', icone: FlaskConical },
  diagnostic: { libelle: 'Diagnostic', icone: Stethoscope },
  traitement: { libelle: 'Traitement', icone: Pill },
} as const

export default function Timeline({
  evenements,
  actions,
}: {
  evenements: EvenementTimeline[]
  actions?: React.ReactNode
}) {
  const [filtre, setFiltre] = useState<keyof typeof NATURES | 'tout'>('tout')
  const [documentOuvert, setDocumentOuvert] = useState<{ id: string; page: number | null } | null>(
    null,
  )

  const visibles = filtre === 'tout' ? evenements : evenements.filter((e) => e.nature === filtre)

  return (
    <Carte
      titre="Histoire de la maladie et chronologie"
      icone={<Activity className="h-[17px] w-[17px] text-bleu-500" />}
      compte={evenements.length}
      actions={actions}
    >
      <div className="flex flex-wrap gap-1.5 px-5 pb-3">
        <Filtre actif={filtre === 'tout'} surClic={() => setFiltre('tout')}>
          Tout
        </Filtre>
        {(Object.keys(NATURES) as (keyof typeof NATURES)[]).map((nature) => {
          const nombre = evenements.filter((e) => e.nature === nature).length
          if (nombre === 0) return null
          return (
            <Filtre key={nature} actif={filtre === nature} surClic={() => setFiltre(nature)}>
              {NATURES[nature].libelle} {nombre}
            </Filtre>
          )
        })}
      </div>

      {visibles.length === 0 ? (
        <EtatVide texte="Rien dans la chronologie pour l’instant." />
      ) : (
        <ol className="px-5 pb-5">
          {visibles.map((evenement) => {
            const Icone = NATURES[evenement.nature].icone
            const premiereSource = evenement.sources[0]
            return (
              <li key={evenement.id} className="flex gap-3.5">
                <div className="flex flex-col items-center">
                  <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-bleu-50 text-bleu-600">
                    <Icone className="h-[15px] w-[15px]" />
                  </span>
                  <span className="my-1 w-px flex-1 bg-trait" />
                </div>

                <div className="min-w-0 flex-1 pt-1 pb-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[12.5px] font-medium text-bleu-700">
                      {f.dateSelonPrecision(evenement.date, evenement.precisionDate)}
                    </span>
                    {evenement.precisionDate === 'INCERTAINE' && (
                      <Pastille ton="attention">date incertaine</Pastille>
                    )}
                  </div>

                  {/* L'intitulé ouvre le document quand il y en a un. Sans
                      source, il reste du texte : rien à ouvrir, rien à cliquer. */}
                  {premiereSource ? (
                    <button
                      className="mt-0.5 block text-left text-[14px] text-encre hover:text-bleu-700"
                      onClick={() =>
                        setDocumentOuvert({
                          id: premiereSource.documentId,
                          page: premiereSource.page,
                        })
                      }
                      title={`Ouvrir ${premiereSource.titre}`}
                    >
                      {evenement.libelle}
                    </button>
                  ) : (
                    <p className="mt-0.5 text-[14px] text-encre">{evenement.libelle}</p>
                  )}

                  {evenement.details && (
                    <p className="mt-0.5 text-[12.5px] text-ardoise">{evenement.details}</p>
                  )}
                </div>
              </li>
            )
          })}
        </ol>
      )}

      {documentOuvert && (
        <VisionneuseDocument
          documentId={documentOuvert.id}
          page={documentOuvert.page}
          fermer={() => setDocumentOuvert(null)}
        />
      )}
    </Carte>
  )
}

function Filtre({
  actif,
  surClic,
  children,
}: {
  actif: boolean
  surClic: () => void
  children: React.ReactNode
}) {
  return (
    <button
      className={`rounded-full px-2.5 py-1 text-[12px] transition-colors ${
        actif ? 'bg-bleu-600 text-white' : 'bg-slate-100 text-ardoise hover:bg-slate-200'
      }`}
      onClick={surClic}
    >
      {children}
    </button>
  )
}
